// src/components/common/Modal.js
// Reusable Tailwind modal — drops the react-bootstrap Modal dependency
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Props:
 *   show        {boolean}  – whether modal is visible
 *   onHide      {function} – called when backdrop or ✕ is clicked
 *   title       {string}   – header title text
 *   size        {'sm'|'md'|'lg'|'xl'} – width preset (default 'md')
 *   children    {node}     – body content
 *   footer      {node}     – footer content (buttons, etc.)
 *   hideClose      {boolean}  – hide the ✕ button
 *   preventClose   {boolean}  – block backdrop, Escape, and ✕ close
 */

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

const Modal = ({
  show,
  onHide,
  title,
  size = 'md',
  children,
  footer,
  hideClose = false,
  preventClose = false,
}) => {
  // Lock body scroll while modal open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [show]);

  // Close on Escape key
  useEffect(() => {
    if (!show) return;
    const handleKey = (e) => {
      if (e.key === 'Escape' && !preventClose) onHide?.();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [show, onHide]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={preventClose ? undefined : onHide}
        aria-hidden="true"
      />

      {/* Dialog panel */}
      <div
        className={`relative w-full ${sizeClasses[size]} bg-white rounded-xl shadow-large overflow-hidden flex flex-col max-h-[90vh]`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || !hideClose) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            {title && (
              <h3 className="text-lg font-semibold text-text-primary m-0">{title}</h3>
            )}
            {!hideClose && !preventClose && (
              <button
                onClick={onHide}
                aria-label="Close modal"
                className="ml-auto text-gray-400 hover:text-gray-600 transition-colors duration-200 bg-transparent border-none cursor-pointer p-1 rounded-md hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-wrap">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

Modal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func,
  title: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  children: PropTypes.node,
  footer: PropTypes.node,
  hideClose: PropTypes.bool,
  preventClose: PropTypes.bool,
};

export default Modal;
